class BryantHuisTheme {
  constructor() {
    this.bindMobileNavigation();
    this.bindMegaMenu();
    this.bindLocalization();
    this.bindAutoSubmit();
    this.bindProductForms();
    this.bindFocusMessages();
    this.loadProductRecommendations();
  }

  bindMobileNavigation() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-mobile-toggle]');
      if (!button) return;
      const navigation = document.getElementById(button.getAttribute('aria-controls'));
      if (!navigation) return;
      const willOpen = navigation.hidden;
      navigation.hidden = !willOpen;
      button.setAttribute('aria-expanded', String(willOpen));
      if (willOpen) {
        const firstLink = navigation.querySelector('a, summary');
        if (firstLink) firstLink.focus();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const button = document.querySelector('[data-mobile-toggle][aria-expanded="true"]');
      const navigation = document.querySelector('[data-mobile-nav]:not([hidden])');
      if (button && navigation) {
        navigation.hidden = true;
        button.setAttribute('aria-expanded', 'false');
        button.focus();
      }
    });
  }

  bindMegaMenu() {
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const active = document.activeElement;
      const menuItem = active && active.closest ? active.closest('.nav-item.has-dropdown') : null;
      if (!menuItem) return;
      const topLink = menuItem.querySelector('.nav-top-link');
      if (topLink) topLink.focus();
    });
  }

  bindLocalization() {
    document.querySelectorAll('[data-localization-select]').forEach((select) => {
      select.addEventListener('change', () => select.form.submit());
    });
  }

  bindAutoSubmit() {
    document.querySelectorAll('[data-auto-submit]').forEach((control) => {
      control.addEventListener('change', () => control.form.submit());
    });
  }

  bindFocusMessages() {
    const message = document.querySelector('[data-focus-message]');
    if (message) message.focus();
  }

  bindProductForms() {
    document.querySelectorAll('[data-product-section]').forEach((section) => {
      const variantScript = section.querySelector('[data-product-variants]');
      const form = section.querySelector('[data-product-form]');
      if (!variantScript || !form) return;

      let variants = [];
      try { variants = JSON.parse(variantScript.textContent); } catch (error) { return; }

      const optionInputs = Array.from(form.querySelectorAll('.option-input'));
      const variantInput = form.querySelector('[data-variant-id]');
      const addButton = form.querySelector('[data-add-to-cart]');
      const addText = form.querySelector('[data-add-to-cart-text]');
      const price = section.querySelector('[data-product-price]');
      const comparePrice = section.querySelector('[data-compare-price]');
      const sku = section.querySelector('[data-product-sku]');
      const unitPrice = section.querySelector('[data-unit-price]');
      const quantity = form.querySelector('input[name="quantity"]');
      const status = section.querySelector('[data-product-status]');
      const pickup = section.querySelector('[data-pickup-availability]');
      const timezoneOffset = form.querySelector('[data-timezone-offset]');
      if (timezoneOffset) timezoneOffset.value = String(new Date().getTimezoneOffset());

      const updateVariant = () => {
        const selected = [];
        const positions = [...new Set(optionInputs.map((input) => Number(input.dataset.optionPosition)))];
        positions.forEach((position) => {
          const checked = form.querySelector(`.option-input[data-option-position="${position}"]:checked`);
          if (checked) selected.push(checked.value);
        });
        const variant = variants.find((candidate) => candidate.options.every((value, index) => value === selected[index])) || variants.find((candidate) => candidate.available);
        if (!variant) return;

        variantInput.value = variant.id;
        if (price) price.textContent = this.formatMoney(variant.price);
        if (comparePrice) {
          comparePrice.textContent = variant.compare_at_price ? this.formatMoney(variant.compare_at_price) : '';
          comparePrice.classList.toggle('hidden', !variant.compare_at_price || variant.compare_at_price <= variant.price);
        }
        if (sku) sku.textContent = variant.sku || '';
        if (addButton) addButton.disabled = !variant.available;
        if (addText && addButton) addText.textContent = variant.available ? addButton.dataset.addLabel : addButton.dataset.soldLabel;
        this.updateUnitPrice(unitPrice, variant);
        if (quantity && variant.inventory_management && variant.inventory_policy === 'deny') quantity.max = Math.max(1, variant.inventory_quantity || 1);
        if (status) status.textContent = variant.available ? `Selected ${variant.title}` : `${variant.title} is sold out`;

        if (variant.featured_media && variant.featured_media.id) {
          const media = section.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (media) media.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest' });
        }

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
        this.updatePickupAvailability(pickup, variant.id);
      };

      optionInputs.forEach((input) => input.addEventListener('change', updateVariant));
      if (variantInput && pickup) this.updatePickupAvailability(pickup, variantInput.value);
    });
  }

  formatMoney(cents) {
    if (window.Shopify && Shopify.currency && Shopify.currency.active) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: Shopify.currency.active }).format(cents / 100);
    }
    return (cents / 100).toFixed(2);
  }


  updateUnitPrice(container, variant) {
    if (!container) return;
    const measurement = variant.unit_price_measurement;
    if (!variant.unit_price || !measurement) { container.innerHTML = ''; return; }
    let reference = measurement.reference_value && measurement.reference_value !== 1 ? measurement.reference_value : '';
    const unit = measurement.reference_unit || '';
    const label = container.dataset.unitPriceLabel || 'Unit price';
    container.textContent = `${label}: ${this.formatMoney(variant.unit_price)} / ${reference}${unit}`;
  }

  updatePickupAvailability(container, variantId) {
    if (!container || !variantId) return;
    const root = window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/';
    fetch(`${root}variants/${variantId}/?section_id=pickup-availability`)
      .then((response) => response.text())
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const section = parsed.querySelector('#shopify-section-pickup-availability');
        container.innerHTML = section ? section.innerHTML : '';
      })
      .catch(() => { container.innerHTML = ''; });
  }

  loadProductRecommendations() {
    document.querySelectorAll('product-recommendations[data-url]').forEach((element) => {
      if (element.innerHTML.trim()) return;
      fetch(element.dataset.url)
        .then((response) => response.text())
        .then((html) => {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const replacement = parsed.querySelector('product-recommendations');
          if (replacement && replacement.innerHTML.trim()) element.innerHTML = replacement.innerHTML;
        })
        .catch(() => {});
    });
  }
}

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('#predictive-search');
    if (!this.input || !this.results) return;
    this.input.addEventListener('input', this.debounce(() => this.onChange(), 250));
    this.input.addEventListener('keydown', (event) => { if (event.key === 'Escape') this.close(); });
  }

  onChange() {
    const term = this.input.value.trim();
    if (!term) { this.close(); return; }
    this.getResults(term);
  }

  getResults(term) {
    const root = window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/';
    const url = `${root}search/suggest?q=${encodeURIComponent(term)}&resources[type]=product,collection,page,article&resources[limit]=6&section_id=predictive-search`;
    fetch(url)
      .then((response) => { if (!response.ok) throw new Error(response.status); return response.text(); })
      .then((text) => {
        const documentFragment = new DOMParser().parseFromString(text, 'text/html');
        const section = documentFragment.querySelector('#shopify-section-predictive-search');
        if (!section) { this.close(); return; }
        this.results.innerHTML = section.innerHTML;
        this.open();
      })
      .catch(() => this.close());
  }

  open() { this.results.hidden = false; this.input.setAttribute('aria-expanded', 'true'); }
  close() { this.results.hidden = true; this.input.setAttribute('aria-expanded', 'false'); }
  debounce(callback, wait) { let timeout; return (...args) => { clearTimeout(timeout); timeout = setTimeout(() => callback.apply(this, args), wait); }; }
}

if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
document.addEventListener('DOMContentLoaded', () => new BryantHuisTheme());